import React, { useEffect, useState, useRef } from 'react';

/**
 * iter156as: Banner de erro de gateway/Cloudflare — moderno, centralizado,
 * com auto-dismiss e botao Tentar Novamente. Substitui os toasts crus com
 * mensagens tecnicas em ingles ("The origin web server returned an invalid...").
 *
 * Uso: montado uma unica vez em App.js. Ouve `window.addEventListener('app:gateway-error')`
 * disparado pelo interceptor de axios em lib/api.js.
 */
export default function GatewayErrorBanner() {
  const [state, setState] = useState({ open: false, title: '', detail: '', status: 0 });
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);
  const dismissRef = useRef(null);
  const retryRef = useRef(null);

  useEffect(() => {
    const onErr = (e) => {
      const d = (e && e.detail) || {};
      // Debounce: se ja esta aberto, apenas reinicia o countdown
      setState({
        open: true,
        title: d.title || 'Servidor indisponivel',
        detail: d.detail || 'Tente novamente em instantes.',
        status: d.status || 0,
      });
      // countdown para retry automatico
      setCountdown(6);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) {
            clearInterval(timerRef.current);
            timerRef.current = null;
            return 0;
          }
          return c - 1;
        });
      }, 1000);
      // auto dismiss em 10s se o usuario nao interagir
      if (dismissRef.current) clearTimeout(dismissRef.current);
      dismissRef.current = setTimeout(() => setState((s) => ({ ...s, open: false })), 10000);
    };
    window.addEventListener('app:gateway-error', onErr);
    return () => {
      window.removeEventListener('app:gateway-error', onErr);
      if (timerRef.current) clearInterval(timerRef.current);
      if (dismissRef.current) clearTimeout(dismissRef.current);
    };
  }, []);

  useEffect(() => {
    // ao abrir, foca no botao retry para acessibilidade
    if (state.open && retryRef.current) {
      try { retryRef.current.focus(); } catch (_) {}
    }
  }, [state.open]);

  if (!state.open) return null;

  const close = () => {
    setState((s) => ({ ...s, open: false }));
    if (timerRef.current) clearInterval(timerRef.current);
  };
  const retry = () => {
    close();
    // aguarda um instante e recarrega — a rota atual mantem-se via SPA state
    setTimeout(() => window.location.reload(), 120);
  };

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="gwerr-title"
      data-testid="gateway-error-banner"
    >
      {/* backdrop com blur */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-in fade-in" onClick={close} />
      {/* card */}
      <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 slide-in-from-bottom-2 duration-300 border border-rose-100">
        {/* faixa superior colorida */}
        <div className="h-1.5 bg-gradient-to-r from-rose-500 via-amber-500 to-rose-500" />
        <div className="p-6 sm:p-7">
          <div className="flex items-start gap-4">
            {/* icone animado */}
            <div className="flex-shrink-0 relative">
              <div className="absolute inset-0 bg-rose-100 rounded-full animate-ping opacity-60" />
              <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center shadow-lg">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7 text-white">
                  <path d="M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z" />
                  <path d="M12 8v4" /><path d="M12 16h.01" />
                </svg>
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <h2 id="gwerr-title" className="text-lg font-black text-slate-900 leading-tight">
                {state.title}
              </h2>
              {state.status ? (
                <span className="inline-block mt-1 text-[10px] font-mono px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200">
                  cod {state.status}
                </span>
              ) : null}
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{state.detail}</p>
              <p className="mt-2 text-xs text-slate-400">
                Isso costuma ser resolvido em poucos segundos. Se persistir, seus dados ficam a salvo — nada foi perdido.
              </p>
            </div>
          </div>
          {/* acoes */}
          <div className="mt-6 flex items-center gap-3">
            <button
              ref={retryRef}
              onClick={retry}
              data-testid="gateway-error-retry"
              className="flex-1 bg-gradient-to-br from-rose-600 to-red-600 hover:from-rose-700 hover:to-red-700 text-white font-bold text-sm px-5 py-3 rounded-xl shadow-lg shadow-rose-600/20 transition-all hover:shadow-rose-600/40 hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="w-4 h-4">
                <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
              </svg>
              Tentar novamente{countdown ? ` (${countdown}s)` : ''}
            </button>
            <button
              onClick={close}
              data-testid="gateway-error-close"
              className="px-4 py-3 rounded-xl text-sm font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
